import React, { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Navbar } from "../components/Navbar";
import { useAuth } from "../context/AuthContext";
import { api, API, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Progress } from "../components/ui/progress";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Upload, Wand2, Loader2, Film, Droplet, Sparkles, CheckCircle2, AlertTriangle,
  XCircle, Link2, Settings2, Coins,
} from "lucide-react";
import { toast } from "sonner";
import { ClipCard } from "../components/ClipCard";

const STAGES = ["QUEUED", "TRANSCRIBE", "ANALYSE", "GENERATE_CANDIDATES", "RENDER", "FINALISE"];

export default function Workspace() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { isPaid, subscription, loadSubscription } = useAuth();

  const [videos, setVideos] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [watermarks, setWatermarks] = useState([]);
  const [clips, setClips] = useState([]);
  const [jobs, setJobs] = useState([]);

  const [videoId, setVideoId] = useState("");
  const [campaignId, setCampaignId] = useState(params.get("campaign") || "none");
  const [aspect, setAspect] = useState("9:16");
  const [numClips, setNumClips] = useState("3");
  const [caption, setCaption] = useState(true);
  const [watermarkId, setWatermarkId] = useState("none");
  const [overlay, setOverlay] = useState("");
  const [advanced, setAdvanced] = useState(false);

  const [uploading, setUploading] = useState(false);
  const [videoUrl, setVideoUrl] = useState("");
  const [job, setJob] = useState(null);
  const pollRef = useRef(null);
  const fileRef = useRef(null);
  const wmRef = useRef(null);

  const loadData = useCallback(() => {
    api.get("/videos").then((r) => setVideos(r.data.videos || [])).catch(() => {});
    api.get("/my-campaigns").then((r) => setCampaigns([...(r.data.saved || []), ...(r.data.imported || [])])).catch(() => {});
    api.get("/watermarks").then((r) => setWatermarks(r.data.watermarks || [])).catch(() => {});
    api.get("/clips").then((r) => setClips(r.data.clips || [])).catch(() => {});
    api.get("/jobs").then((r) => setJobs(r.data.jobs || [])).catch(() => {});
  }, []);

  useEffect(() => { if (isPaid) loadData(); }, [isPaid, loadData]);

  const pollJob = useCallback((jobId) => {
    clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await api.get(`/jobs/${jobId}`);
        setJob(data);
        if (data.status === "completed" || data.status === "failed") {
          clearInterval(pollRef.current);
          loadData();
          loadSubscription();
          if (data.status === "completed") toast.success(data.message);
          else toast.error(data.message || "Processing failed");
        }
      } catch {
        clearInterval(pollRef.current);
      }
    }, 1500);
  }, [loadData, loadSubscription]);

  useEffect(() => () => clearInterval(pollRef.current), []);

  if (!isPaid) {
    return (
      <div className="min-h-screen grain">
        <Navbar />
        <div className="max-w-lg mx-auto px-6 py-24 text-center">
          <div className="w-16 h-16 rounded-2xl bg-amber-500 flex items-center justify-center amber-glow mx-auto mb-6">
            <Sparkles className="w-8 h-8 text-zinc-950" />
          </div>
          <h1 className="font-display text-3xl font-bold mb-3">Unlock AI Clipping</h1>
          <p className="text-zinc-400 mb-8">
            Upload videos, analyse footage, generate clips, add captions, apply campaign requirements
            and export your finished clips with a Clipstar subscription.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button onClick={() => navigate("/pricing")} data-testid="ws-view-plans" className="bg-amber-500 hover:bg-amber-400 text-zinc-950 font-bold">View Plans</Button>
            <Button onClick={() => navigate("/campaigns")} variant="outline" className="border-white/10">Browse Campaigns</Button>
          </div>
        </div>
      </div>
    );
  }

  const uploadVideo = async (file) => {
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/video/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Video uploaded");
      setVideos((v) => [data, ...v]);
      setVideoId(data.id);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const submitUrl = async () => {
    if (!videoUrl.trim()) return;
    setUploading(true);
    try {
      const { data } = await api.post("/video/url", { url: videoUrl });
      toast.success("Video fetched");
      setVideos((v) => [data, ...v]);
      setVideoId(data.id);
      setVideoUrl("");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't fetch URL");
    } finally {
      setUploading(false);
    }
  };

  const uploadWatermark = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    fd.append("name", file.name);
    fd.append("position", "bottom-right");
    try {
      const { data } = await api.post("/watermarks", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Watermark added");
      setWatermarks((w) => [data, ...w]);
      setWatermarkId(data.id);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Upload failed");
    }
  };

  const generate = async () => {
    if (!videoId) { toast.error("Select or upload a video first"); return; }
    try {
      const { data } = await api.post("/clips/generate", {
        video_id: videoId,
        campaign_id: campaignId === "none" ? null : campaignId,
        aspect_ratio: aspect,
        num_clips: parseInt(numClips),
        watermark_id: watermarkId === "none" ? null : watermarkId,
        overlay_text: overlay || null,
        caption,
      });
      setJob(data);
      toast.success("Processing started");
      pollJob(data.id);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to start");
    }
  };

  const stageIdx = job ? STAGES.indexOf(job.stage) : -1;

  return (
    <div className="min-h-screen grain">
      <Navbar />
      <div className="max-w-6xl mx-auto px-6 py-10">
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="inline-flex px-2.5 py-1 rounded-full bg-amber-500/10 text-amber-400 border border-amber-500/30 text-[10px] font-mono uppercase tracking-widest mb-2">Paid Pro</div>
            <h1 className="font-display text-3xl font-bold">AI Clipping Workspace</h1>
          </div>
          <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-amber-500/10 border border-amber-500/30">
            <Coins className="w-4 h-4 text-amber-400" />
            <span className="text-sm font-semibold text-amber-300">{subscription?.credits ?? 0}</span>
          </div>
        </div>

        <Tabs defaultValue="create">
          <TabsList className="bg-[#141419] border border-white/10">
            <TabsTrigger value="create" data-testid="ws-tab-create"><Wand2 className="w-4 h-4 mr-1.5" /> Create</TabsTrigger>
            <TabsTrigger value="jobs" data-testid="ws-tab-jobs"><Loader2 className="w-4 h-4 mr-1.5" /> Jobs ({jobs.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="create" className="mt-6">
            <div className="grid lg:grid-cols-3 gap-6">
              {/* left: source + options */}
              <div className="lg:col-span-2 space-y-6">
                {/* upload */}
                <div className="bg-[#141419] border border-white/10 rounded-2xl p-6">
                  <h2 className="font-display font-bold mb-4 flex items-center gap-2"><Upload className="w-4 h-4 text-amber-400" /> 1. Add footage</h2>
                  <div
                    onClick={() => fileRef.current?.click()}
                    data-testid="upload-dropzone"
                    className="border-2 border-dashed border-white/15 rounded-xl p-8 text-center cursor-pointer hover:border-amber-500/40 transition-colors"
                  >
                    {uploading ? <Loader2 className="w-6 h-6 animate-spin text-amber-400 mx-auto" /> : (
                      <>
                        <Upload className="w-8 h-8 text-zinc-500 mx-auto mb-2" />
                        <p className="text-sm text-zinc-400">Click to upload MP4, MOV or WebM</p>
                      </>
                    )}
                    <input ref={fileRef} type="file" accept=".mp4,.mov,.webm,video/*" hidden data-testid="upload-video-input" onChange={(e) => uploadVideo(e.target.files[0])} />
                  </div>
                  <div className="flex items-center gap-2 mt-3">
                    <Link2 className="w-4 h-4 text-zinc-500" />
                    <Input data-testid="video-url-input" value={videoUrl} onChange={(e) => setVideoUrl(e.target.value)} placeholder="Or paste a direct video URL" className="bg-zinc-900 border-white/10" />
                    <Button onClick={submitUrl} disabled={uploading} data-testid="video-url-submit" variant="outline" className="border-white/10 shrink-0">Fetch</Button>
                  </div>
                  {videos.length > 0 && (
                    <div className="mt-4">
                      <Label className="text-xs text-zinc-500">Select source video</Label>
                      <div className="grid grid-cols-1 gap-2 mt-2">
                        {videos.map((v) => (
                          <button
                            key={v.id}
                            onClick={() => setVideoId(v.id)}
                            data-testid={`video-select-${v.id}`}
                            className={`flex items-center justify-between p-3 rounded-lg border text-left text-sm transition-colors ${videoId === v.id ? "border-amber-500/50 bg-amber-500/5" : "border-white/10 hover:border-white/20"}`}
                          >
                            <span className="flex items-center gap-2 truncate"><Film className="w-4 h-4 text-zinc-500 shrink-0" /> <span className="truncate">{v.original_filename}</span></span>
                            <span className="text-zinc-500 shrink-0 ml-2">{Math.round(v.duration)}s · {v.width}×{v.height}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* campaign + options */}
                <div className="bg-[#141419] border border-white/10 rounded-2xl p-6">
                  <h2 className="font-display font-bold mb-4 flex items-center gap-2"><Sparkles className="w-4 h-4 text-amber-400" /> 2. Configure</h2>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-xs text-zinc-400">Campaign (applies requirements)</Label>
                      <Select value={campaignId} onValueChange={setCampaignId}>
                        <SelectTrigger data-testid="select-campaign" className="bg-zinc-900 border-white/10 mt-1.5"><SelectValue placeholder="No campaign" /></SelectTrigger>
                        <SelectContent className="bg-zinc-900 border-white/10">
                          <SelectItem value="none">No campaign</SelectItem>
                          {campaigns.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs text-zinc-400">Aspect ratio</Label>
                      <Select value={aspect} onValueChange={setAspect}>
                        <SelectTrigger data-testid="select-aspect" className="bg-zinc-900 border-white/10 mt-1.5"><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-zinc-900 border-white/10">
                          <SelectItem value="9:16">9:16 Vertical</SelectItem>
                          <SelectItem value="1:1">1:1 Square</SelectItem>
                          <SelectItem value="16:9">16:9 Landscape</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs text-zinc-400">Number of clips</Label>
                      <Select value={numClips} onValueChange={setNumClips}>
                        <SelectTrigger data-testid="select-numclips" className="bg-zinc-900 border-white/10 mt-1.5"><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-zinc-900 border-white/10">
                          {["1", "2", "3", "4", "5"].map((n) => <SelectItem key={n} value={n}>{n}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex items-center justify-between pt-6">
                      <Label className="text-sm text-zinc-300">Burned-in captions</Label>
                      <Switch checked={caption} onCheckedChange={setCaption} data-testid="toggle-caption" />
                    </div>
                  </div>

                  <button onClick={() => setAdvanced(!advanced)} className="flex items-center gap-1.5 text-xs text-zinc-500 hover:text-zinc-300 mt-4">
                    <Settings2 className="w-3.5 h-3.5" /> Advanced Settings
                  </button>
                  {advanced && (
                    <div className="mt-3 space-y-4 pt-3 border-t border-white/5">
                      <div>
                        <Label className="text-xs text-zinc-400">Text overlay (campaign-required text)</Label>
                        <Input data-testid="overlay-input" value={overlay} onChange={(e) => setOverlay(e.target.value)} placeholder="e.g. @brandname or campaign name" className="bg-zinc-900 border-white/10 mt-1.5" />
                      </div>
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <Label className="text-xs text-zinc-400">Watermark</Label>
                          <button onClick={() => wmRef.current?.click()} data-testid="add-watermark-btn" className="text-xs text-amber-400 hover:text-amber-300 flex items-center gap-1"><Droplet className="w-3 h-3" /> Add</button>
                          <input ref={wmRef} type="file" accept=".png,.jpg,.jpeg,.svg" hidden onChange={(e) => uploadWatermark(e.target.files[0])} />
                        </div>
                        <Select value={watermarkId} onValueChange={setWatermarkId}>
                          <SelectTrigger data-testid="select-watermark" className="bg-zinc-900 border-white/10"><SelectValue placeholder="No watermark" /></SelectTrigger>
                          <SelectContent className="bg-zinc-900 border-white/10">
                            <SelectItem value="none">No watermark</SelectItem>
                            {watermarks.map((w) => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  )}
                </div>

                <Button
                  onClick={generate}
                  disabled={!videoId || (job && job.status === "processing")}
                  data-testid="generate-clips-btn"
                  className="w-full h-12 bg-amber-500 hover:bg-amber-400 text-zinc-950 font-bold text-base amber-glow"
                >
                  <Wand2 className="w-5 h-5 mr-2" /> Generate Clips
                </Button>
              </div>

              {/* right: progress */}
              <div className="space-y-4">
                <div className="bg-[#141419] border border-white/10 rounded-2xl p-6 sticky top-24">
                  <h3 className="font-display font-bold mb-4">Processing pipeline</h3>
                  {!job ? (
                    <p className="text-sm text-zinc-500">Start a generation to see live progress here.</p>
                  ) : (
                    <div data-testid="job-progress">
                      <Progress value={job.progress} className="h-2 mb-3" />
                      <p className="text-sm text-amber-400 font-medium mb-4">{job.message}</p>
                      <div className="space-y-2">
                        {STAGES.map((s, i) => (
                          <div key={s} className={`flex items-center gap-2 text-xs ${i <= stageIdx || job.status === "completed" ? "text-emerald-400" : "text-zinc-600"}`}>
                            {job.status === "completed" || i < stageIdx ? <CheckCircle2 className="w-3.5 h-3.5" /> : i === stageIdx ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <div className="w-3.5 h-3.5 rounded-full border border-current" />}
                            {s.replace("_", " ")}
                          </div>
                        ))}
                      </div>
                      {job.status === "failed" && <p className="text-xs text-red-400 mt-3">{job.error}</p>}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* results */}
            {clips.length > 0 && (
              <div className="mt-10">
                <h2 className="font-display text-xl font-bold mb-4">Recent clips</h2>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {clips.slice(0, 6).map((c) => <ClipCard key={c.id} clip={c} onChange={loadData} />)}
                </div>
              </div>
            )}
          </TabsContent>

          <TabsContent value="jobs" className="mt-6">
            {jobs.length === 0 ? (
              <p className="text-zinc-500 text-center py-16">No processing jobs yet.</p>
            ) : (
              <div className="space-y-3" data-testid="jobs-list">
                {jobs.map((j) => (
                  <div key={j.id} className="bg-[#141419] border border-white/10 rounded-xl p-5">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium text-sm">{j.message}</span>
                      <Badge className={`text-[10px] ${j.status === "completed" ? "bg-emerald-500/15 text-emerald-400" : j.status === "failed" ? "bg-red-500/15 text-red-400" : "bg-amber-500/15 text-amber-400"}`}>
                        {j.status}
                      </Badge>
                    </div>
                    <Progress value={j.progress} className="h-1.5" />
                    <p className="text-xs text-zinc-500 mt-2">{j.stage} · {j.clip_ids?.length || 0} clips</p>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
